import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle, User, Camera, Layers, UploadCloud, X, Loader2, Crown, ArrowLeft, Sparkles,
} from "lucide-react";
import { API, authHeaders, useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { toast } from "sonner";
import { LogIn } from "lucide-react";

const MAX_FILES = 4;
const MAX_BYTES = 8 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const SECTION_ORDER = [
  "title", "summary", "tags",
  "personality", "attachment_style",
  "green_flags", "red_flags",
  "compatibility", "long_term_prediction",
  "approach_strategy", "diagnosis", "final_recommendation",
];

const SECTION_LABELS = {
  title: "Tytuł", summary: "Podsumowanie", tags: "Tagi",
  personality: "Profil psychologiczny", attachment_style: "Styl przywiązania",
  green_flags: "Green flags", red_flags: "Red flags",
  compatibility: "Kompatybilność", long_term_prediction: "Predykcja długoterminowa",
  approach_strategy: "Strategia podejścia", diagnosis: "Diagnoza",
  final_recommendation: "Rekomendacja końcowa",
};

function compressImage(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error("compress failed"));
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
        }, "image/jpeg", quality);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function AnalyzePremium() {
  const { t, lang } = useLang();
  const { user, ready, refreshEntitlements } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const pl = lang === "pl";

  const [mode, setMode] = useState("full");
  const [contactName, setContactName] = useState("");
  const [text, setText] = useState("");
  const [aboutYou, setAboutYou] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [ent, setEnt] = useState({ credits: 0, subscription_active: false });

  // streaming state
  const [streamSections, setStreamSections] = useState({}); // key -> value
  const [streamOrder, setStreamOrder] = useState([]); // arrival order
  const [streamPhase, setStreamPhase] = useState("idle");

  useEffect(() => {
    axios.get(`${API}/payments/entitlements`, { headers: authHeaders() })
      .then(r => setEnt(r.data))
      .catch(() => {});
  }, []);

  // Login gate: rating another person is a paid, per-account feature.
  if (ready && !user) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center" data-testid="premium-login-gate">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-3xl dc-gradient-bg shadow-[0_12px_40px_rgba(253,38,122,0.4)]">
          <Crown className="h-7 w-7 text-white" />
        </div>
        <h1 className="font-display text-2xl font-bold text-white">
          {pl ? "Oceń drugą osobę" : "Rate another person"}
        </h1>
        <p className="mt-3 text-sm text-[#a8adbd]">
          {pl ? "To funkcja premium. Zaloguj się lub załóż konto, aby Twoje kredyty były zapisane."
              : "This is a premium feature. Sign in or create an account so your credits are saved."}
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <button onClick={() => navigate("/login?next=/analyze/premium")} data-testid="gate-login-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full dc-gradient-bg px-6 py-3 font-display text-sm font-semibold text-white">
            <LogIn className="h-4 w-4" />{pl ? "Zaloguj się" : "Sign in"}
          </button>
          <button onClick={() => navigate("/register")} data-testid="gate-register-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#222636] bg-[#181b24] px-6 py-3 font-display text-sm font-semibold text-white">
            {pl ? "Załóż konto" : "Create account"}
          </button>
        </div>
      </div>
    );
  }

  const modes = [
    { key: "conversation", icon: MessageCircle, label: t("modeConversation") },
    { key: "profile", icon: User, label: t("modeProfile") },
    { key: "photos", icon: Camera, label: t("modePhotos") },
    { key: "full", icon: Layers, label: t("modeFull") },
  ];

  const onFiles = async (incoming) => {
    const accepted = [];
    for (const f of Array.from(incoming || [])) {
      if (!ALLOWED.includes(f.type)) { toast.error(`${f.name}: unsupported`); continue; }
      if (f.size > MAX_BYTES * 2) { toast.error(`${f.name}: too large`); continue; }
      try {
        const c = await compressImage(f);
        accepted.push({ file: c, preview: URL.createObjectURL(c) });
      } catch { toast.error(`${f.name}: compress failed`); }
      if (files.length + accepted.length >= MAX_FILES) break;
    }
    setFiles((prev) => [...prev, ...accepted].slice(0, MAX_FILES));
  };

  const removeFile = (i) => {
    setFiles((prev) => {
      URL.revokeObjectURL(prev[i]?.preview);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const noEntitlement =
    Number(ent.credits || 0) < 2 && !ent.subscription_active;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() && files.length === 0) {
      toast.error(t("contextPlaceholder"));
      return;
    }
    if (noEntitlement) {
      toast.error(t("needCredits"));
      navigate("/premium");
      return;
    }

    setLoading(true);
    setStreamSections({});
    setStreamOrder([]);
    setStreamPhase("starting");

    try {
      const fd = new FormData();
      fd.append("mode", mode);
      fd.append("contact_name", contactName);
      fd.append("text", text);
      fd.append("about_you", aboutYou);
      fd.append("language", lang);
      files.forEach(({ file }) => fd.append("images", file));

      const token = localStorage.getItem("dc_token");
      const res = await fetch(`${API}/analyze/premium/stream`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        if (res.status === 402) {
          toast.error(t("needCredits"));
          navigate("/premium");
          return;
        }
        const detail = await res.text();
        throw new Error(detail || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let reportId = null;
      let streamError = null;

      setStreamPhase("streaming");
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = block.split("\n");
          let event = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
          if (!data) continue;
          let payload;
          try { payload = JSON.parse(data); } catch { continue; }
          if (event === "section" && payload.key) {
            setStreamSections((prev) => ({ ...prev, [payload.key]: payload.value }));
            setStreamOrder((prev) => prev.includes(payload.key) ? prev : [...prev, payload.key]);
          } else if (event === "done") {
            reportId = payload.id;
            setStreamPhase("done");
          } else if (event === "error") {
            streamError = payload.detail || "AI error";
          }
        }
      }

      if (streamError) throw new Error(streamError);
      if (reportId) {
        try {
          const fresh = await refreshEntitlements();
          if (fresh) setEnt(fresh);
        } catch {/* non-blocking */}
        setTimeout(() => navigate(`/report/${reportId}`), 800);
      } else {
        throw new Error("Stream finished without report id");
      }
    } catch (err) {
      toast.error(err.message || t("analysisError"));
      setStreamPhase("error");
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
      <button onClick={() => navigate("/dashboard")} data-testid="premium-analyze-back" className="mb-6 inline-flex items-center gap-2 text-sm text-[#a8adbd] hover:text-white">
        <ArrowLeft className="h-4 w-4" />{t("backToDashboard")}
      </button>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="inline-flex items-center gap-2 rounded-full border border-[#fdb022]/30 bg-[#fdb022]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#fdb022]">
          <Crown className="h-3.5 w-3.5" />Premium
        </div>
        <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl" data-testid="other-analyze-title">
          {pl ? "Oceń drugą osobę" : "Rate another person"}
        </h1>
        <p className="mt-2 text-sm text-[#a8adbd]">
          {pl ? "Pełna ocena drugiej osoby: zdjęcia, opis i profil psychologiczny. Funkcja płatna."
              : "Full evaluation of another person: photos, description and psychological profile. Paid feature."}
        </p>

        <div className="mt-4 inline-flex items-center gap-3 rounded-2xl border border-[#222636] bg-[#181b24] px-4 py-2 text-sm" data-testid="premium-entitlement-bar">
          <span className="text-[#a8adbd]">{t("creditsBalance")}:</span>
          <b className="font-display text-base dc-gradient-text">{ent.credits}</b>
          {ent.subscription_active && (
            <span className="rounded-full bg-[#32d583]/15 px-2 py-0.5 text-xs font-semibold text-[#32d583]">
              {t("subscriptionActive")}
            </span>
          )}
        </div>
      </motion.div>

      <form onSubmit={onSubmit} className="mt-8 space-y-6" data-testid="premium-analyze-form">
        <section className="dc-card p-6 sm:p-8">
          <h2 className="mb-4 font-display text-lg font-semibold text-white">{t("analysisMode")}</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {modes.map((m) => {
              const Icon = m.icon;
              const active = mode === m.key;
              return (
                <button type="button" key={m.key} onClick={() => setMode(m.key)} data-testid={`premium-mode-${m.key}`}
                  className={`rounded-2xl border p-4 text-left transition-all ${active ? "border-[#ff4458] bg-gradient-to-br from-[#ff4458]/10 to-[#ff6036]/5" : "border-[#222636] bg-[#0f1117] hover:border-[#ff4458]/40"}`}>
                  <Icon className={`mb-2 h-5 w-5 ${active ? "text-[#ff4458]" : "text-[#a8adbd]"}`} />
                  <div className="font-display text-sm font-semibold text-white">{m.label}</div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="dc-card p-6 sm:p-8">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">{t("contactName")}</label>
          <input data-testid="premium-contact-name" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder={t("contactPlaceholder")}
            className="w-full rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/20" />
        </section>

        <section className="dc-card p-6 sm:p-8">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">{t("contextText")}</label>
          <textarea data-testid="premium-context-text" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("contextPlaceholder")} rows={7}
            className="w-full resize-y rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/20" />
        </section>

        <section className="dc-card relative overflow-hidden p-6 sm:p-8">
          <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-[#ff4458]/20 to-transparent blur-2xl" />
          <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#fdb022]">
            <Sparkles className="h-3.5 w-3.5" />{t("aboutYou")}
          </label>
          <textarea data-testid="premium-about-you" value={aboutYou} onChange={(e) => setAboutYou(e.target.value)} placeholder={t("aboutYouPlaceholder")} rows={4}
            className="w-full resize-y rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-3 text-white outline-none focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/20" />
        </section>

        <section className="dc-card p-6 sm:p-8">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">{t("uploadTitle")}</label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            data-testid="premium-upload-zone"
            className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-all ${dragOver ? "border-[#ff4458] bg-[#ff4458]/5" : "border-[#222636] hover:border-[#ff4458]/60"}`}>
            <UploadCloud className="mx-auto mb-3 h-8 w-8 text-[#ff4458]" />
            <div className="font-display font-semibold text-white">{t("uploadHint")}</div>
            <div className="mt-1 text-xs text-[#a8adbd]">{t("uploadMax")}</div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple className="hidden" data-testid="premium-file-input"
              onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
          </div>
          {files.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {files.map((f, i) => (
                <div key={i} className="group relative overflow-hidden rounded-2xl border border-[#222636]">
                  <img src={f.preview} alt="" className="aspect-square w-full object-cover" />
                  <button type="button" onClick={() => removeFile(i)} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[#0f1117]/85 text-white opacity-0 group-hover:opacity-100">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {noEntitlement && (
          <div className="dc-card border-[#fdb022]/50 bg-[#fdb022]/5 p-5 text-center" data-testid="no-credits-notice">
            <p className="text-sm text-[#fdb022]">{t("needCredits")}</p>
            <button type="button" onClick={() => navigate("/premium")} className="mt-3 inline-flex rounded-full dc-gradient-bg px-5 py-2 font-display text-sm font-semibold text-white">
              {t("goToPremium")}
            </button>
          </div>
        )}

        <button type="submit" disabled={loading || noEntitlement} data-testid="premium-analyze-submit"
          className="flex w-full items-center justify-center gap-2 rounded-full dc-gradient-bg px-6 py-4 font-display text-base font-semibold text-white shadow-[0_12px_36px_rgba(253,38,122,0.35)] transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60">
          {loading ? (<><Loader2 className="h-5 w-5 animate-spin" />{streamPhase === "streaming" ? t("streamingProgress") : t("analyzing")}</>) : (<><Crown className="h-5 w-5" />{t("analyzePremium")}</>)}
        </button>

        {/* Progressive section reveal */}
        {(loading || streamOrder.length > 0) && (
          <section data-testid="premium-stream-board" className="dc-card relative overflow-hidden p-5 sm:p-7">
            <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-gradient-to-br from-[#ff4458]/20 via-[#fdb022]/15 to-transparent blur-3xl" />
            <div className="relative">
              <div className="mb-2 flex items-center gap-2">
                <span className="relative inline-flex h-2 w-2">
                  <span className={`absolute inline-flex h-full w-full rounded-full bg-[#ff4458]/70 ${streamPhase === "streaming" ? "animate-ping" : ""}`} />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#ff4458]" />
                </span>
                <span className="font-display text-sm font-semibold text-white">
                  {streamPhase === "done" ? t("streamingDone") : t("premiumStreamWaiting")}
                </span>
                <span className="ml-auto rounded-full bg-[#222636] px-2 py-0.5 font-mono text-[10px] text-[#a8adbd]" data-testid="premium-stream-counter">
                  {streamOrder.length}/{SECTION_ORDER.length}
                </span>
              </div>
              <p className="text-xs text-[#a8adbd]">{t("premiumStreamReveal")}</p>

              <div className="mt-5 space-y-3">
                <AnimatePresence initial={false}>
                  {streamOrder.map((key) => (
                    <motion.div
                      key={key}
                      initial={{ opacity: 0, y: 12, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      data-testid={`premium-stream-section-${key}`}
                      className="rounded-2xl border border-[#222636] bg-[#0f1117] p-4"
                    >
                      <div className="mb-1.5 flex items-center gap-2">
                        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[#32d583]" />
                        <span className="text-xs font-bold uppercase tracking-wider dc-gradient-text">{SECTION_LABELS[key] || key}</span>
                      </div>
                      <SectionPreview value={streamSections[key]} />
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Upcoming placeholders */}
                {streamPhase !== "done" && SECTION_ORDER.filter(k => !streamOrder.includes(k)).slice(0, 3).map((key) => (
                  <div
                    key={`skel-${key}`}
                    data-testid={`premium-stream-skel-${key}`}
                    className="flex items-center gap-3 rounded-2xl border border-dashed border-[#222636]/60 bg-[#0f1117]/50 p-4"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-[#222636]" />
                    <span className="text-xs uppercase tracking-wider text-[#222636]">{SECTION_LABELS[key] || key}</span>
                    <span className="ml-auto h-1.5 w-20 animate-pulse rounded-full bg-[#222636]" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </form>
    </div>
  );
}

function SectionPreview({ value }) {
  if (value == null) return <p className="text-sm text-[#a8adbd]">—</p>;
  if (typeof value === "string") {
    return <p className="line-clamp-3 text-sm leading-relaxed text-white">{value}</p>;
  }
  if (Array.isArray(value)) {
    return (
      <ul className="space-y-1">
        {value.slice(0, 4).map((v, i) => (
          <li key={i} className="flex gap-2 text-sm leading-relaxed text-white">
            <span className="mt-1.5 h-1 w-1 rounded-full bg-[#ff4458]" />
            <span className="line-clamp-2">{typeof v === "string" ? v : JSON.stringify(v)}</span>
          </li>
        ))}
        {value.length > 4 && <li className="text-xs text-[#a8adbd]">+{value.length - 4}</li>}
      </ul>
    );
  }
  if (typeof value === "object") {
    const entries = Object.entries(value).slice(0, 4);
    return (
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {entries.map(([k, v]) => (
          <div key={k} className="text-xs">
            <span className="text-[#a8adbd]">{k}: </span>
            <span className="text-white">{typeof v === "string" || typeof v === "number" ? v : JSON.stringify(v).slice(0, 60)}</span>
          </div>
        ))}
      </div>
    );
  }
  return <p className="text-sm text-white">{String(value)}</p>;
}
